import { useStampStore } from '@/store/stamp';
import { useTaskStore } from '@/store/task';
import { i18n } from '@/i18n';
import { computed, ComputedRef, ref } from '@vue/composition-api';
import { CouponState, ExActAndConfigCouponInfo } from '@/types/coupon';
import { storeToRefs } from 'pinia';
import { CustomException } from '@tencent/ppd-base-libs';

/** 根据印花是否足够来判断展示的文案
 * 用于 CouponState.pover 以及 CouponState.unable的情况下
 */
const getDialogBtnText = (isEnoughStamp) => {
  if (!isEnoughStamp) {
    return i18n('立即儲印花');
  }
  return i18n('我知道了');
};

/** 按钮上方提示枚举 */
enum AboveTip {
  /** 需要多少印花 */
  prerequisite = 'prerequisite',
  /** 还差多少印花 */
  stillNeed = 'stillNeed',
  /** 空 */
  none = 'none'
};

/** 按钮下面提示枚举 */
enum UnderTip {
  /** 无库存 */
  outOfStock = 'outOfStock',
  /** 用户达限额 */
  userLimit = 'userLimit',
  /** 活动日限额 */
  dailyLimit = 'dailyLimit',
  /** 无 */
  none = 'none'
}

const aboveTip: Record<AboveTip, string> = {
  [AboveTip.prerequisite]: i18n('需要'),
  [AboveTip.stillNeed]: i18n('還差'),
  [AboveTip.none]: '',
};

const underTip: Record<UnderTip, string> = {
  [UnderTip.outOfStock]: i18n('已兌完'),
  [UnderTip.userLimit]: i18n('已達兌換上限'),
  [UnderTip.dailyLimit]: i18n('今日獎賞已兌完'),
  [UnderTip.none]: '',
};

/** 不同优惠券状态对应不同的弹窗表现【策略模式】
 * function的优先级要比string的高
 */
const couponStatusStrategy: Record<string, {
  /** 按钮文案 */
  btnText: string,
  /** 按钮文案方法 */
  getBtnText?: (isEnoughStamp: boolean, isLock: boolean) => string,
  /** 按钮disabled */
  btnDisabled: boolean,
  /** 按钮disabled方法 */
  getBtnDisabled?: (isEnoughStamp: boolean, isLock: boolean) => boolean
  /** 按钮上方提示 */
  aboveTip: AboveTip,
  /** 按钮下方提示 */
  underTip: UnderTip,
  /** 是否需要打开任务弹窗，引导用户完成任务 */
  isShowTask: boolean,
}> = {
  /** 售罄 */
  [CouponState.end]: {
    btnText: i18n('我知道了'),
    btnDisabled: true,
    aboveTip: AboveTip.prerequisite,
    underTip: UnderTip.outOfStock,
    isShowTask: false,
  },
  /** 个人超限 */
  [CouponState.over]: {
    btnText: i18n('我知道了'),
    btnDisabled: true,
    aboveTip: AboveTip.prerequisite,
    underTip: UnderTip.userLimit,
    isShowTask: false,
  },
  /** 平台当日限额 */
  [CouponState.pover]: {
    btnText: i18n('我知道了'),
    getBtnText: getDialogBtnText,
    btnDisabled: false,
    getBtnDisabled: (isEnoughStamp) => isEnoughStamp,
    aboveTip: AboveTip.stillNeed,
    underTip: UnderTip.dailyLimit,
    isShowTask: true,
  },
  /** 印花不足 */
  [CouponState.unable]: {
    btnText: i18n('我知道了'),
    getBtnText: getDialogBtnText,
    btnDisabled: false,
    aboveTip: AboveTip.stillNeed,
    underTip: UnderTip.none,
    isShowTask: true,
  },
  /** 可领取 */
  [CouponState.enable]: {
    btnText: i18n('立即換領'),
    getBtnText: (isEnoughStamp, isLock) => {
      if (isLock) {
        return i18n('我知道了');
      }
      return i18n('立即換領');
    },
    btnDisabled: false,
    getBtnDisabled: (isEnoughStamp, isLock) => isLock,
    aboveTip: AboveTip.prerequisite,
    underTip: UnderTip.none,
    isShowTask: false,
  },
  /** 已过期 */
  [CouponState.expired]: {
    btnText: i18n('我知道了'),
    btnDisabled: true,
    aboveTip: AboveTip.prerequisite,
    underTip: UnderTip.none,
    isShowTask: false,
  },
  default: {
    btnText: i18n('我知道了'),
    btnDisabled: true,
    aboveTip: AboveTip.prerequisite,
    underTip: UnderTip.none,
    isShowTask: false,
  },
};

export const useCouponDetailDialog = (prop: {
  /** 券信息 */
  couponInfo: ExActAndConfigCouponInfo,
  // 是否锁
  isLock: boolean
}) => {
  const stampStore = useStampStore();
  const { isCannoDoMoreTask: isCannotDoMoreTask } = storeToRefs(useTaskStore());
  const { remainCount } = storeToRefs(stampStore);

  /** 是否能够兑换奖品
   * 券状态为可领取并且没有上锁才能进行兑换动作
   */
  const isAllowExchangePrize: ComputedRef<boolean> =
    computed(() => prop.couponInfo.couponState === CouponState.enable && !prop.isLock);

  /** 当前用户印花是否不够兑换对应选中的优惠券 */
  const isStampsNotEnough: ComputedRef<boolean> =
    computed(() => prop.couponInfo.exQualCnt - remainCount.value > 0);

  /** 是否有未完成的任务 */
  const isHasUnfinishedTasks: ComputedRef<boolean> =
    computed(() => !isCannotDoMoreTask.value);

  /** 是否需要打开任务半屏弹窗，引导用户完成任务获取印花
   * 条件：有未完成的任务 && 用户印花不足兑换 && 当前状态是否为需要展示
   */
  const isShowTaskList: ComputedRef<boolean> =
    computed(() => dialogBtnConfig.value.isShowTask && isHasUnfinishedTasks.value && isStampsNotEnough.value);

  /** 半屏弹窗按钮上方提示文案 */
  const getAboveTip = () => {
    const base = couponStatusStrategy[prop.couponInfo.couponState];
    /** 如果提示为仍需要的话 需要查看是否印花足够，如果足够仍然展示一共需要多少个，不足才展示还需要多少 */
    if (base.aboveTip === AboveTip.stillNeed) {
      return !isStampsNotEnough.value ? {
        text: aboveTip.prerequisite,
        value: prop.couponInfo.exQualCnt,
      } : {
        text: aboveTip.stillNeed,
        value: prop.couponInfo.exQualCnt - remainCount.value,
      };
    }

    if (base.aboveTip === AboveTip.prerequisite) {
      return {
        text: aboveTip[base.aboveTip],
        value: prop.couponInfo.exQualCnt,
      };
    }
    return null;
  };

  /** 半屏弹窗按钮下方提示文案 */
  const getUnderTip = () => {
    const base = couponStatusStrategy[prop.couponInfo.couponState];
    if (prop.isLock) {
      return i18n('中午12:00開始兌換');
    }
    return underTip[base.underTip];
  };

  /** 获取配置方法 */
  const getDialogBtnConfig = () => {
    let base = couponStatusStrategy[prop.couponInfo.couponState];
    if (!base) {
      /** 如果状态没有命中枚举的话就告警并使用过期 */
      new CustomException(prop.couponInfo, 'getDialogBtnConfgFail', `get dialog btn config fail, coupon state: ${prop.couponInfo.couponState}, isLock: ${prop.isLock}`);
      base = couponStatusStrategy.default;
    }
    return {
      btnDisabled: base.getBtnDisabled ? base.getBtnDisabled(!isStampsNotEnough.value, prop.isLock) : base.btnDisabled,
      btnText: base.getBtnText ? base.getBtnText(!isStampsNotEnough.value, prop.isLock) : base.btnText,
      aboveTip: getAboveTip(),
      underTip: getUnderTip(),
      isShowTask: base.isShowTask,
    };
  };

  /** 弹窗按钮相关配置对象 */
  const dialogBtnConfig = computed(getDialogBtnConfig);
  /** 按钮是否被禁用 */
  const isDialogBtnDisabled = computed(() => dialogBtnConfig.value.btnDisabled);

  /** 弹窗标题 */
  const titleText: ComputedRef<string> = computed(() => {
    if (isAllowExchangePrize.value) {
      return i18n('確認換領');
    }
    return i18n('優惠券詳情');
  });
  // 券详情弹框处理
  const isShowCouponInfoDialog = ref(false);

  function showCouponInfoDialog() {
    isShowCouponInfoDialog.value = true;
  }

  function hideCouponInfoDialog() {
    isShowCouponInfoDialog.value = false;
  }

  return {
    titleText,
    isAllowExchangePrize,
    isShowTaskList,
    dialogBtnConfig,
    isDialogBtnDisabled,
    isShowCouponInfoDialog,
    showCouponInfoDialog,
    hideCouponInfoDialog,
  };
};
